package com.patronat.top.root

import com.arkivanov.decompose.ComponentContext
import com.arkivanov.decompose.router.stack.*
import com.arkivanov.decompose.value.Value
import com.patronat.top.core.auth.TokenStorage
import com.patronat.top.dto.UserRole
import com.patronat.top.features.auth.presentation.AuthComponent
import com.patronat.top.features.auth.presentation.DefaultAuthComponent
import com.patronat.top.features.dashboard.presentation.DefaultMainComponent
import com.patronat.top.features.dashboard.presentation.MainComponent
import io.github.aakira.napier.Napier
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable

class DefaultRootComponent(
    componentContext: ComponentContext,
    private val tokenStorage: TokenStorage
) : ComponentContext by componentContext {

    private val navigation = StackNavigation<Config>()

    private val scope = CoroutineScope(Dispatchers.Main)

    val childStack: Value<ChildStack<*, Child>> = childStack(
        source = navigation,
        serializer = Config.serializer(),
        initialConfiguration = Config.Splash,
        handleBackButton = true,
        childFactory = ::createChild
    )

    init {

        checkSession()
    }

    private fun checkSession() {
        scope.launch {
            delay(500)
            val token = tokenStorage.getAccessToken()
            val role = tokenStorage.getRole()

            if (token != null && role != null) {
                Napier.i(tag = "RootComponent") { "Session restored. Role: $role. Navigating to Main." }
                navigation.replaceAll(Config.Main(role))
            } else {
                Napier.i(tag = "RootComponent") { "No active session. Navigating to Auth." }
                navigation.replaceAll(Config.Auth)
            }
        }
    }

    private fun createChild(config: Config, context: ComponentContext): Child {
        return when (config) {
            is Config.Splash -> Child.Splash

            is Config.Auth -> Child.Auth(
                DefaultAuthComponent(
                    componentContext = context,
                    onLoginSuccess = { role ->
                        navigation.replaceAll(Config.Main(role))
                    }
                )
            )

            is Config.Main -> Child.Main(
                DefaultMainComponent(
                    componentContext = context,
                    userRole = config.role,
                    onLogout = {
                        scope.launch {
                            tokenStorage.clear()
                            Napier.i(tag = "RootComponent") { "Session cleared. Navigating to Auth." }
                            navigation.replaceAll(Config.Auth)
                        }
                    }
                )
            )
        }
    }

    sealed class Child {
        data object Splash : Child()
        class Auth(val component: AuthComponent) : Child()
        class Main(val component: MainComponent) : Child()
    }

    @Serializable
    sealed interface Config {
        @Serializable data object Splash : Config
        @Serializable data object Auth : Config
        @Serializable data class Main(val role: UserRole) : Config
    }
}